import { cn } from '@/lib/utils';

interface AppleHealthAttributionProps {
  className?: string;
  /** Source app name from Apple Health (e.g. "Apple Watch"). */
  sourceApp?: string | null;
}

/** Apple Health heart icon matching their brand color. */
function AppleHealthHeartIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="#FF2D55"
      className={className}
      aria-hidden
    >
      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
    </svg>
  );
}

/**
 * Required attribution when displaying Apple Health-sourced activity data.
 * Shows the Apple Health heart icon + "Activity data provided by Apple Health via HealthKit".
 */
export function AppleHealthAttribution({ className, sourceApp }: AppleHealthAttributionProps) {
  const source = sourceApp || 'Apple Health';

  return (
    <div
      className={cn('flex items-center gap-1.5 text-[10px] text-muted-foreground leading-tight', className)}
      role="note"
      aria-label={`Activity data provided by ${source} via HealthKit`}
    >
      <AppleHealthHeartIcon className="h-3 w-3 shrink-0" />
      <span>Data provided by {source} via HealthKit</span>
    </div>
  );
}

/**
 * Attribution for derived/analytics views (PMC, coaching insights, AI analysis).
 * Required on any screen showing data derived from Apple Health metrics.
 */
export function AppleHealthDerivedAttribution({ className }: { className?: string }) {
  return (
    <div
      className={cn('flex items-center gap-1.5 text-[9px] text-muted-foreground/70 leading-tight', className)}
      role="note"
      aria-label="Insights derived in part from Apple Health data via HealthKit"
    >
      <AppleHealthHeartIcon className="h-2.5 w-2.5 shrink-0 opacity-50" />
      <span>Insights derived in part from Apple Health data via HealthKit</span>
    </div>
  );
}
